import { useMemo } from 'react';
import { css, hstack } from '@bts-web/utils-style-engine';
import { decodeBase64String } from '@bts-web/utils-functions';
import { BuySellMoreButton } from '../BuySellMoreButton/BuySellMoreButton';
import { CopyToClipboard } from '../../common/components';
import {
  CryptoActionViewQueryData,
  CryptoTransfersTransactionsItemType,
  FiatTransactionViewQueryData,
  SwapOfferViewQueryData,
  TransactionViewQueryData,
} from '../types';
import {
  SavingsPlanTransactionItemType,
  StakingTransactionItemType,
} from '@bts-web/core-features/transactions';
import { CryptoTransactionInfoStatus } from '../../crypto-transfer-details/utils/getCryptoTransferCryptoTransferDetailsLabelsByStatus';
import { VerifyWalletButton } from '../VerifyWalletButton/VerifyWalletButton';
import { getRoundedPrecision } from '@bts-web/utils-formatting';
import { TransactionDirection } from '../../transactions/types/transactions';
import {
  isBuyOrSellTransaction,
  isCorporateActionViewQueryNode,
  isCryptoActionViewQueryNode,
  isCryptoTransferTransaction,
  isFiatTransactionViewQueryNode,
  isStakingActionViewQueryNode,
  isSwapViewQueryNode,
  isUnstakingActionViewQueryNode,
} from '../utils/transactionDetailsUtils';

type TransactionDetailsFooterProps = {
  transaction:
    | TransactionViewQueryData
    | CryptoTransfersTransactionsItemType
    | SavingsPlanTransactionItemType
    | StakingTransactionItemType
    | SwapOfferViewQueryData
    | CryptoActionViewQueryData
    | FiatTransactionViewQueryData;
  translations: {
    buyMore: string;
    sellMore: string;
    pid?: string;
    orderIdCopySuccessMessage: string;
    orderIdCopyFailedMessage: string;
    copy: string;
    verifyWallet: string;
  };
  showActionButton?: boolean;
};

const footerStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 'medium',
  gap: 'medium',
});

const TransactionDetailsFooter = ({
  transaction,
  translations,
  showActionButton = true,
}: TransactionDetailsFooterProps) => {
  const isSwapTransaction = isSwapViewQueryNode(transaction);

  const isCryptoActionTransaction = isCryptoActionViewQueryNode(transaction);

  const isCorporateActionTransaction =
    isCorporateActionViewQueryNode(transaction);

  const isStakingTransaction =
    isStakingActionViewQueryNode(transaction) ||
    isUnstakingActionViewQueryNode(transaction);

  const isSellBuyTransaction = isBuyOrSellTransaction(transaction);

  const { __typename } = transaction || {};

  const isSellType = __typename === 'SellTransaction';

  const getPid = () => {
    if (isSwapTransaction || isCryptoActionTransaction) {
      const { childrenTransactions } = transaction || {};

      const outgoingSwapTransaction =
        (childrenTransactions &&
          childrenTransactions?.length > 1 &&
          childrenTransactions.find(
            (item) => item.direction === TransactionDirection.OUTGOING,
          )) ||
        null;

      return (
        outgoingSwapTransaction?.id &&
        decodeBase64String(outgoingSwapTransaction?.id).split(':')[1]
      );
    } else {
      return (
        transaction?.id && decodeBase64String(transaction.id).split(':')[1]
      );
    }
  };

  const isSellAllowed = useMemo(() => {
    if (isSellBuyTransaction) {
      const { asset } = transaction || {};

      const { minimumSellAmount } = asset || {};

      const fiatBalanceAmount =
        asset?.portfolio &&
        Number(getRoundedPrecision(asset?.portfolio?.fiatBalance));

      const minimumSellAmountNumber = Number(
        getRoundedPrecision(minimumSellAmount),
      );

      return Boolean(
        isSellType &&
          fiatBalanceAmount &&
          fiatBalanceAmount > minimumSellAmountNumber,
      );
    }

    return false;
  }, [__typename]);

  const pidElement = transaction?.id && (
    <div
      className={hstack({
        overflow: 'hidden',
        fontSize: 'label.medium_medium',
        justifyContent: 'space-between',
        flex: 'auto',
        width: '100%',
      })}
    >
      <div
        role="presentation"
        className={css({
          flex: 1,
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          minWidth: 0,
          color: 'neutrals.text_secondary',
          fontSize: 'caption.small',
          fontWeight: 'caption.small',
          lineHeight: 'caption.small',
          letterSpacing: 'caption.small',
        })}
      >
        {`${translations.pid} ${getPid()}`}
      </div>

      <CopyToClipboard
        value={getPid() as string}
        translations={{
          successMessage: translations.orderIdCopySuccessMessage,
          failedMessage: translations.orderIdCopyFailedMessage,
        }}
      />
    </div>
  );

  const actionButton = useMemo(() => {
    if (
      isSwapTransaction ||
      isCryptoActionTransaction ||
      isCorporateActionTransaction ||
      isStakingTransaction ||
      isFiatTransactionViewQueryNode(transaction) ||
      !showActionButton
    ) {
      return null;
    }

    if (
      isCryptoTransferTransaction(transaction) &&
      transaction?.status === CryptoTransactionInfoStatus.ON_HOLD
    ) {
      return (
        <VerifyWalletButton transaction={transaction}>
          {translations.verifyWallet}
        </VerifyWalletButton>
      );
    }

    return (
      transaction?.asset && (
        <BuySellMoreButton
          assetId={transaction?.asset?.id as string}
          isSell={isSellAllowed}
        >
          {isSellAllowed ? translations.sellMore : translations.buyMore}
        </BuySellMoreButton>
      )
    );
  }, []);

  return (
    <div
      className={css({
        width: '100%',
        marginTop: 'auto',
      })}
      data-testid="transaction-details-footer"
    >
      <footer className={footerStyles}>
        {actionButton}
        {pidElement}
      </footer>
    </div>
  );
};

export { TransactionDetailsFooter };
